import { useMemo } from "react";
import { capitalize } from "lodash";
import { FilterTagWrapper } from "@/components/tags";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import type { Filter } from "@/components/tags/types";
import { FilterApplicableFor } from "@/components/tags/types";
import styles from "../../styles.module.scss";

interface UserOption {
  label: string;
  value: number | string;
}

interface ExplorerFilterTagsProps {
  userList: UserOption[];
}

export const ExplorerFilterTags = ({ userList }: ExplorerFilterTagsProps) => {
  const { datastores = [] } = useAppState();
  const { searchParams, deleteSearchParamsByKeyValue } = useFiltersBySearchParams();

  const filters = useMemo(() => {
    return Array.from(searchParams.keys()).reduce((acc: Record<string, Filter>, key: string) => {
      if (
        key === "start_date" ||
        key === "end_date" ||
        key === "period" ||
        key === "sort" ||
        key === "page" ||
        key === "size" ||
        key === "tab"
      ) {
        return acc;
      }

      if (key === "instance") {
        const values = searchParams.getAll(key);
        acc[`${capitalize(key)}Filter`] = {
          filterStr: values.map(
            value => datastores.find(d => d.instance_id === value)?.name || value
          ),
          label: capitalize(key),
          onclose: (value?: string) => {
            if (!value) return;
            // Try to find by name first, fallback to treating value as instance_id
            const instanceId = datastores.find(d => d.name === value)?.instance_id || value;
            deleteSearchParamsByKeyValue({
              [key]: instanceId,
            });
          },
        };
        return acc;
      }

      if (key === "owner") {
        const values = searchParams.getAll(key);
        acc[`${capitalize(key)}`] = {
          filterStr: values.map(value => {
            const user = userList.find(u => u.value.toString() === value);
            return user?.label || value;
          }),
          filterData: { [key]: values },
          label: capitalize(key),
          onclose: (value?: string) => {
            if (!value) return;
            const userId = userList.find(u => u.label === value)?.value;
            deleteSearchParamsByKeyValue({ [key]: userId?.toString() });
          },
        };
        return acc;
      }

      const values = searchParams.getAll(key);
      acc[`${capitalize(key)}Filter`] = {
        filterStr: values,
        label: capitalize(key),
        filterData: { [key]: values },
        onclose: (value?: string) => {
          if (!value) return;
          deleteSearchParamsByKeyValue({ [key]: value });
        },
      };
      return acc;
    }, {});
  }, [searchParams, userList, datastores, deleteSearchParamsByKeyValue]);

  return (
    <div className={styles.filterTags}>
      <FilterTagWrapper filters={filters} applicableFor={FilterApplicableFor.DISCOVER} />
    </div>
  );
};
